/**
 * Every campaign — each value a campaign has gone by, from wherever one is
 * recorded, in one table: the people it brought (first touches), follows of
 * our published links that carried it, Meta clicks with an `fbclid`, and the
 * links labelled with it. There is no campaign registry; this is the union.
 *
 * A campaign whose ads send `{{campaign.id}}` arrives as a number. It can be
 * named here once, and the name shows wherever the value does. Backend
 * `admin/analytics/campaigns.ts`.
 */
import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Badge, Button, DataTable, InfoTip, type ColumnDef, type SortState } from '@shared-ui/components';
import { campaignsApi, looksLikeId, type CampaignRow } from '../../../api/campaignsApi';

const KIND: Record<CampaignRow['kind'], { label: string; variant: 'default' | 'warning' | 'success'; tip: string }> = {
  'meta-ad': { label: 'Meta ad', variant: 'success', tip: 'Tagged paid from Instagram or Facebook, or clicked with an fbclid' },
  'link-label': { label: 'Link label', variant: 'default', tip: 'The campaign a published link is labelled with' },
  'tagged-url': { label: 'Tagged URL', variant: 'warning', tip: 'Only seen in a URL’s utm_campaign' },
};

function fmtWhen(iso?: string): string {
  if (!iso) return '—';
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${pad(d.getMonth() + 1)}/${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/** The name, or an inline box to give one. */
function NameCell({ row }: { row: CampaignRow }) {
  const qc = useQueryClient();
  const [draft, setDraft] = useState<string | null>(null);
  const save = useMutation({
    mutationFn: (name: string) => campaignsApi.setName(row.campaign, name),
    onSuccess: () => {
      setDraft(null);
      void qc.invalidateQueries({ queryKey: ['campaigns'] });
      void qc.invalidateQueries({ queryKey: ['campaign-names'] });
    },
  });
  if (draft !== null) {
    return (
      <span className="cp-name-edit" onClick={e => e.stopPropagation()}>
        <input autoFocus value={draft} placeholder="e.g. Physics sims – Sep" onChange={e => setDraft(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') save.mutate(draft); if (e.key === 'Escape') setDraft(null); }} />
        <Button size="sm" variant="primary" disabled={save.isPending} onClick={() => save.mutate(draft)}>Save</Button>
        <Button size="sm" variant="secondary" onClick={() => setDraft(null)}>Cancel</Button>
      </span>
    );
  }
  return (
    <span className="cp-name">
      {row.name ? <strong>{row.name}</strong> : looksLikeId(row.campaign) ? <em className="cp-unnamed">unnamed id</em> : <strong>{row.campaign}</strong>}
      {(row.name || looksLikeId(row.campaign)) && <span className="cp-id">{row.campaign}</span>}
      {(row.name || looksLikeId(row.campaign)) && (
        <button type="button" className="cp-rename" onClick={e => { e.stopPropagation(); setDraft(row.name ?? ''); }}>
          {row.name ? 'Rename' : 'Name it'}
        </button>
      )}
    </span>
  );
}

export default function CampaignsPanel({ from, to }: { from: Date; to: Date }) {
  const { data, isLoading, error } = useQuery({
    queryKey: ['campaigns', from.toISOString(), to.toISOString()],
    queryFn: () => campaignsApi.list(from, to),
  });
  const [sort, setSort] = useState<SortState>({ sortBy: 'newPeople', sortOrder: 'desc' });
  const [query, setQuery] = useState('');
  const rows = useMemo(() => {
    const q = query.trim().toLowerCase();
    return (data?.campaigns ?? []).filter(r => !q || `${r.campaign} ${r.name ?? ''} ${r.links.join(' ')}`.toLowerCase().includes(q));
  }, [data, query]);

  const num = (key: keyof CampaignRow, header: React.ReactNode): ColumnDef<CampaignRow> => ({
    key, header, sortable: true, className: 'sa-num',
    sortValue: r => r[key] as number,
    render: r => ((r[key] as number) ? (r[key] as number).toLocaleString() : <span className="sa-zero">0</span>),
  });
  const columns: ColumnDef<CampaignRow>[] = [
    { key: 'campaign', header: 'Campaign', sortable: true, hideable: false,
      sortValue: r => (r.name ?? r.campaign).toLowerCase(), render: r => <NameCell row={r} /> },
    { key: 'kind', header: 'Kind', sortable: true, sortValue: r => r.kind,
      render: r => <span title={KIND[r.kind].tip}><Badge variant={KIND[r.kind].variant} size="sm">{KIND[r.kind].label}</Badge></span> },
    num('newPeople', <span title="New accounts in this window whose first touch carried this campaign — the people it brought">New people</span>),
    num('signedUp', <span title="Of those new people, no longer guests">Signed up</span>),
    num('linkFollows', <span title="Follows of our published links that carried this campaign">Link follows</span>),
    num('metaClicks', <span title="Meta clicks recorded — only arrivals whose URL carried an fbclid">Meta clicks</span>),
    num('withClickId', <span title="Of the new people, whose landing URL carried an ad click id">With click id</span>),
    { key: 'sources', header: 'Source / medium',
      render: r => (r.sources.length
        ? <span title={r.sources.join(', ')}>{r.sources[0]}{r.sources.length > 1 && <span className="sa-zero"> +{r.sources.length - 1}</span>}</span>
        : <span className="sa-zero">—</span>) },
    { key: 'links', header: 'Links labelled',
      render: r => (r.links.length ? <span title={r.links.join(', ')}>{r.links.join(', ')}</span> : <span className="sa-zero">—</span>) },
    { key: 'lastSeenAt', header: 'Last seen', sortable: true, sortValue: r => r.lastSeenAt ?? '',
      render: r => <span className="lp-nowrap" title={r.lastSeenAt}>{fmtWhen(r.lastSeenAt)}</span> },
  ];

  return (
    <section className="sa-panel">
      <h2>
        Campaigns
        <InfoTip>
          <span className="sa-tip-p">
            Every value a campaign has gone by in this window, from all the places one is recorded: new people’s first
            touches, follows of our published links, Meta clicks, and the links labelled with a campaign.
          </span>
          <span className="sa-tip-p">
            <strong>New people</strong> is the number that reflects what a campaign brought. <strong>Meta clicks</strong>
            {' '}counts only arrivals that carried an <code>fbclid</code> — most paid arrivals do not, so it runs far below.
          </span>
          <span className="sa-tip-p">
            A campaign whose ads send <code>{'{{campaign.id}}'}</code> instead of <code>{'{{campaign.name}}'}</code> shows as
            a number. Name it here once; the name shows wherever the value does. Better still, fix the ad’s URL
            parameters (template under Settings → Ad Campaigns).
          </span>
        </InfoTip>
      </h2>
      {error
        ? <p className="sa-error">{(error as Error).message}</p>
        : (
          <DataTable<CampaignRow>
            columns={columns}
            data={rows}
            rowKey={r => r.campaign}
            sort={sort}
            onSortChange={setSort}
            onSearch={setQuery}
            searchPlaceholder="Search campaign, name, link…"
            loading={isLoading}
            compact
            emptyMessage="No campaign was seen in this window."
          />
        )}
    </section>
  );
}
